import React from 'react';
import {
  Button,
  Callout,
  FormFeedback,
  Link,
  Modal,
  TextArea
} from '@morressier/alembic';
import { Flex, Text } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope } from '@fortawesome/pro-light-svg-icons';
import { faExternalLink, faSearch } from '@fortawesome/pro-regular-svg-icons';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { FieldLabel } from 'src/components/Field';
import { inviteReviewerBulkUploadSchema } from '@publishing-engine/domain/validation';

interface Props {
  onClose: () => void;
  onAddContacts: (emails: string[]) => void;
}

type FormData = {
  emails: string;
};

const AddContactsModal: React.FC<Props> = ({ onClose, onAddContacts }) => {
  const methods = useForm({
    defaultValues: {
      emails: ''
    },
    resolver: zodResolver(inviteReviewerBulkUploadSchema)
  });
  const {
    formState: { errors }
  } = methods;
  const emails = methods.watch('emails');

  const onSubmit = (data: FormData) => {
    const lines = data.emails.split('\n');
    const trimmedLines = lines.map(line => line.trim().toLowerCase());

    onAddContacts(trimmedLines);
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      width="720px"
      closeButtonProps={{
        hidden: true
      }}
    >
      <form
        onSubmit={e => {
          // How to prevent the parent form from submitting? Or Nested forms with react-hook-form?
          // #ref: https://github.com/orgs/react-hook-form/discussions/7038#discussioncomment-11376398
          e.stopPropagation();
          e.preventDefault();

          void methods.handleSubmit(onSubmit)(e);
        }}
      >
        <Flex flexDirection="column" gap={6} padding={8}>
          <FontAwesomeIcon
            icon={faEnvelope}
            style={{ width: '40px', height: '40px' }}
          />

          <Flex flexDirection="column" gap={2}>
            <Text textStyle="headingGroup">Invite External Adjudicators</Text>
            <Text textStyle="textBody">
              Add a list of expert emails to populate this screen.
            </Text>
          </Flex>

          {import.meta.env.VITE_CLIENT_REALM === 'bnj' ? null : (
            <Callout
              variant="neutral"
              heading="Find Reviewer Contacts in ATLAS Search"
              customIcon={<FontAwesomeIcon icon={faSearch} />}
              action={
                <Link
                  variant="primary"
                  href="https://atlas-ioporg0.msappproxy.net/#/"
                  isExternal
                >
                  <FontAwesomeIcon
                    icon={faExternalLink}
                    style={{ marginRight: 8 }}
                  />
                  Open ATLAS
                </Link>
              }
            />
          )}

          <Flex flexDirection="column" gap={2}>
            <FieldLabel
              label="Enter Expert Emails"
              subText="Place each email on a separate line to ensure all emails are correctly processed by the system."
              isRequired={false}
            />
            <TextArea
              value={emails}
              placeholder="Enter emails, one per line with no commas"
              {...methods.register('emails')}
              isInvalid={!!errors.emails}
            />
            {errors.emails && (
              <FormFeedback variant="error">
                {errors.emails.message}
              </FormFeedback>
            )}
          </Flex>

          <Flex justifyContent="flex-end" gap={2}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Add Contacts
            </Button>
          </Flex>
        </Flex>
      </form>
    </Modal>
  );
};

export default AddContactsModal;
